import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Divider,
  Switch,
  FormControlLabel,
  InputAdornment,
  Autocomplete,
} from '@mui/material';

import {
  SaveOutlined,
  CancelOutlined,
  AddCircleOutline,
  DeleteOutlined,
  CalculateOutlined,
} from '@mui/icons-material';
import { useClientStore } from '../stores/useClientStore';
import { useQuoteStore } from '../stores/useQuoteStore';
import { useProjetStore } from '../stores/useProjetStore';
import { useProduitCatalogueStore } from '../stores/useProduitCatalogueStore';
import { useNotification } from '../contexts/NotificationContext';
import { Devis, LigneDevis, TypeLigneDevis, StatutDevis, ConsommablesCalcules, TypeRemise } from '../types';
import { CorpsMetier, CORPS_METIER_LABELS } from '../types/subscription';
import { calculateDevisTotals, calculateLigneTotal, formatEuro, calculateDevisTotalsWithRemise } from '../utils/calculations';
import ProjetSelector from '../components/ProjetSelector';
import CalculateurConsommables from '../components/CalculateurConsommables';
import CalculateurConsommablesUniversel from '../components/CalculateurConsommablesUniversel';
import CalculateurTechnique from '../components/CalculateurTechnique';
import RoomTemplatePicker from '../components/RoomTemplatePicker';
import DevisWizard from '../components/DevisWizard';
import { getTaillesByMetier, getDesignationsByMetier, getTailleLabel } from '../config/metiersData';
import { useAuthStore } from '../stores/useAuthStore';
import Alert from '@mui/material/Alert';

type DevisFormData = Omit<Devis, 'id' | 'numero' | 'createdAt' | 'updatedAt' | 'sousTotal' | 'montantHT' | 'montantTVA' | 'totalTTC'>;

// Liste complète des tailles de carrelage standard (en cm) - CONSERVÉE POUR COMPATIBILITÉ
const TAILLES_CARRELAGE = [
  '10x10',
  '15x15',
  '20x20',
  '25x25',
  '30x30',
  '33x33',
  '40x40',
  '45x45',
  '50x50',
  '60x60',
  '75x75',
  '80x80',
  '90x90',
  '100x100',
  '120x120',
  // Formats rectangulaires
  '7.5x15',
  '7.5x30',
  '10x20',
  '15x30',
  '15x60',
  '15x90',
  '20x40',
  '20x60',
  '20x80',
  '20x120',
  '25x40',
  '30x60',
  '30x90',
  '40x80',
  '40x120',
  '45x90',
  '60x120',
  '75x150',
  '80x160',
  '100x100',
  '120x60',
  '120x120',
  '120x240',
  '120x280',
  // Formats parquet/lames
  '15x60',
  '15x90',
  '20x80',
  '20x100',
  '20x120',
  '25x100',
  '25x150',
];

// Liste des désignations courantes pour un devis de carrelage
const DESIGNATIONS_CARRELAGE = [
  // Pose de carrelage
  'Pose de carrelage sol intérieur',
  'Pose de carrelage mural',
  'Pose de carrelage terrasse extérieure',
  'Pose de carrelage balcon',
  'Pose de carrelage escalier',
  'Pose de carrelage garage',
  'Pose de carrelage salle de bain',
  'Pose de carrelage cuisine',

  // Types de carrelage
  'Fourniture et pose carrelage grès cérame',
  'Fourniture et pose carrelage porcelaine',
  'Fourniture et pose carrelage pierre naturelle',
  'Fourniture et pose carrelage imitation parquet',
  'Fourniture et pose carrelage grand format',
  'Fourniture et pose mosaïque',

  // Préparation
  'Ragréage autolissant',
  'Préparation du support',
  'Dépose ancien carrelage',
  'Dépose ancien revêtement',
  'Chape de ravoirage',
  'Ponçage et nettoyage du sol',

  // Plinthes et finitions
  'Pose de plinthes carrelage',
  'Pose de plinthes bois',
  'Pose de baguette d\'angle',
  'Pose de nez de marche',
  'Pose de profilé de finition',

  // Joints
  'Joints de carrelage sol',
  'Joints de carrelage mural',
  'Joint de dilatation',
  'Joint silicone',

  // Faïence
  'Pose de faïence murale',
  'Pose de crédence cuisine',
  'Pose de faïence douche',
  'Pose de faïence baignoire',

  // Étanchéité
  'Étanchéité sous carrelage (SEL)',
  'Pose de bande d\'étanchéité',
  'Traitement des angles',

  // Travaux spécifiques
  'Création de douche à l\'italienne',
  'Pose receveur de douche',
  'Calepinage personnalisé',
  'Pose en chevron',
  'Pose en opus',

  // Fournitures
  'Colle carrelage',
  'Mortier joint',
  'Primaire d\'accrochage',
  'Profilé de dilatation',

  // Déplacement et divers
  'Frais de déplacement',
  'Évacuation gravats',
  'Protection des ouvrages',
  'Nettoyage de fin de chantier',
];

const DevisFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEditing = !!id;

  const { clients, loadClients } = useClientStore();
  const { addDevis, updateDevis, getDevisById } = useQuoteStore();
  const { projets, getProjetById, loadProjets } = useProjetStore();
  const { produits, loadProduits } = useProduitCatalogueStore();
  const { user } = useAuthStore();
  const { showError, showSuccess } = useNotification();

  // Récupérer projetId depuis l'URL
  const projetIdFromUrl = searchParams.get('projetId');
  const initialProjetId = projetIdFromUrl ? parseInt(projetIdFromUrl, 10) : null;

  const [totals, setTotals] = useState({ sousTotal: 0, montantTVA: 0, totalTTC: 0 });
  const [selectedProjetId, setSelectedProjetId] = useState<number | null>(initialProjetId);
  const [projetError, setProjetError] = useState<string>('');
  const [calculateurOpen, setCalculateurOpen] = useState(false);
  const [calculateurConsommablesOpen, setCalculateurConsommablesOpen] = useState(false);
  const [ligneActiveIndex, setLigneActiveIndex] = useState<number | null>(null); // Pour savoir quelle ligne déclenche le modal
  const [surfaceLigneActive, setSurfaceLigneActive] = useState<number>(0);
  const [roomTemplatePickerOpen, setRoomTemplatePickerOpen] = useState(false); // Modal de sélection de templates par pièce
  // Toujours afficher le wizard en mode création (sauf si un projetId est fourni en URL)
  const [showWizard, setShowWizard] = useState(!isEditing); // État pour afficher le wizard

  // Métier sélectionné pour ce devis (auto-détecté depuis le profil utilisateur, mais modifiable)
  const [metierSelectionne, setMetierSelectionne] = useState<CorpsMetier>(
    (user?.corpsMetier as CorpsMetier) || CorpsMetier.CARRELEUR
  );

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<DevisFormData>({
    defaultValues: {
      clientId: 0,
      dateEmission: new Date().toISOString().split('T')[0],
      dateCreation: new Date().toISOString().split('T')[0],
      dateValidite: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      statut: StatutDevis.BROUILLON,
      tauxTVA: user?.isExemptFromTVA ? 0 : 20, // Auto-détection: 0% si franchise en base, 20% sinon
      typeRemise: undefined,
      valeurRemise: undefined,
      lignes: [],
      notes: '',
      conditions: 'Devis valable 30 jours. Acompte de 30% à la commande.',
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'lignes',
  });

  // Observer les changements des lignes et du taux TVA pour l'affichage
  const lignes = watch('lignes');
  const tauxTVA = watch('tauxTVA');

  useEffect(() => {
    loadClients();
    loadProjets();
    loadProduits(); // Charger le catalogue de produits
  }, [loadClients, loadProjets, loadProduits]);

  // Le wizard est toujours affiché en mode création (initialisé dans useState)
  // Pas besoin de useEffect pour la détection, le wizard gère lui-même la logique d'étape

  // Présélectionner le client si un projet est passé en URL (mode création uniquement)
  useEffect(() => {
    if (!isEditing && initialProjetId) {
      const projet = getProjetById(initialProjetId);
      if (projet) {
        setValue('clientId', projet.clientId);
        setProjetError(''); // Clear any error
      }
    }
  }, [initialProjetId, isEditing, getProjetById, setValue]);

  // Charger le devis en mode édition
  useEffect(() => {
    if (isEditing && id) {
      const devisId = parseInt(id, 10);
      const devis = getDevisById(devisId);
      if (devis) {
        // Vérifier si le devis peut être modifié
        if (devis.statut === StatutDevis.ACCEPTE || devis.signatureClient) {
          showError('Ce devis ne peut pas être modifié car il a été accepté ou signé.');
          navigate(`/devis/${devisId}`);
          return;
        }

        const dateCreation = (devis.dateEmission || devis.dateCreation || '').split('T')[0];
        const dateEmission = devis.dateEmission.split('T')[0];
        const dateValidite = devis.dateValidite.split('T')[0];

        reset({
          clientId: devis.clientId,
          dateEmission,
          dateCreation,
          dateValidite,
          statut: devis.statut,
          tauxTVA: devis.tauxTVA,
          typeRemise: devis.typeRemise,
          valeurRemise: devis.valeurRemise,
          lignes: devis.lignes,
          notes: devis.notes,
          conditions: devis.conditions,
        });
      }
    }
  }, [isEditing, id, getDevisById, reset, navigate]);

  // Recalculer les totaux à chaque changement - S'abonner à tous les champs du formulaire
  useEffect(() => {
    const subscription = watch((formData) => {
      const lignes = (formData.lignes || []).filter((ligne): ligne is LigneDevis => ligne !== undefined);
      const tauxTVA = formData.tauxTVA !== undefined ? formData.tauxTVA : 20; // Fix: gérer le cas où tauxTVA = 0
      const typeRemise = formData.typeRemise as TypeRemise | undefined;
      const valeurRemise = formData.valeurRemise;

      // Calculer les totaux avec ou sans remise
      if (typeRemise && valeurRemise && valeurRemise > 0) {
        const calculatedTotals = calculateDevisTotalsWithRemise(lignes, tauxTVA, typeRemise, valeurRemise);
        setTotals({
          sousTotal: calculatedTotals.sousTotal,
          montantTVA: calculatedTotals.montantTVA,
          totalTTC: calculatedTotals.totalTTC,
        });
      } else {
        const calculatedTotals = calculateDevisTotals(lignes, tauxTVA);
        setTotals(calculatedTotals);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  const onSubmit = async (data: DevisFormData) => {
    // Vérifier qu'un projet est sélectionné
    if (!selectedProjetId && !isEditing) {
      setProjetError('Veuillez sélectionner un projet');
      return;
    }

    // S'assurer que les dates sont au bon format (YYYY-MM-DD)
    const dateEmission = data.dateEmission?.includes('T')
      ? data.dateEmission.split('T')[0]
      : data.dateEmission || '';
    const dateCreation = data.dateCreation?.includes('T')
      ? data.dateCreation.split('T')[0]
      : data.dateCreation || '';
    const dateValidite = data.dateValidite.includes('T')
      ? data.dateValidite.split('T')[0]
      : data.dateValidite;

    // Récupérer le projetId (depuis selectedProjetId ou depuis le devis existant)
    let projetId = selectedProjetId;
    if (isEditing && id) {
      const existingDevis = getDevisById(parseInt(id, 10));
      projetId = existingDevis?.projetId || null;
    }

    const devisData: Omit<Devis, 'id' | 'numero' | 'createdAt' | 'updatedAt'> = {
      ...data,
      projetId: projetId || 0,
      dateEmission: `${dateEmission || dateCreation}T00:00:00.000Z`,
      dateCreation: `${dateCreation || dateEmission}T00:00:00.000Z`,
      dateValidite: `${dateValidite}T00:00:00.000Z`,
      clientId: data.clientId,
      sousTotal: totals.sousTotal,
      montantHT: totals.sousTotal,
      montantTVA: totals.montantTVA,
      totalTTC: totals.totalTTC,
    };

    try {
      if (isEditing && id) {
        await updateDevis(parseInt(id, 10), devisData);
        showSuccess('Devis modifié avec succès');
        // Rediriger vers la page de détail du devis modifié
        navigate(`/devis/${id}`);
      } else {
        if (selectedProjetId) {
          const createdDevis = await addDevis(selectedProjetId, devisData);
          showSuccess(`Devis ${createdDevis.numero} créé avec succès`);
          // Rediriger vers la page de détail du devis nouvellement créé
          navigate(`/devis/${createdDevis.id}`);
        }
      }
    } catch (error) {
      console.error('❌ Erreur lors de la soumission du formulaire:', error);
      // Ne pas naviguer en cas d'erreur pour permettre à l'utilisateur de voir le message
    }
  };

  const addLigne = () => {
    const newLigne: LigneDevis = {
      id: `ligne_${Date.now()}`,
      description: '',
      type: TypeLigneDevis.SURFACE,
      modeSaisieSurface: 'direct', // Par défaut: saisie par dimensions
      quantite: 1,
      prixUnitaire: 0,
      total: 0,
      longueur: 0,
      largeur: 0,
      surface: 0,
      avecPertes: false,
    };
    append(newLigne);
  };

  // Fonction pour ajouter automatiquement les consommables comme sous-lignes
  const handleAjouterConsommables = (consommables: ConsommablesCalcules) => {
    if (ligneActiveIndex === null) return;

    // Créer une sous-ligne pour chaque consommable
    const nouvellesSousLignes: LigneDevis[] = consommables.consommables.map((consommable, index) => ({
      id: `sous_ligne_${consommable.nom.replace(/\s+/g, '_')}_${Date.now()}_${index}`,
      description: consommable.nom,
      type: TypeLigneDevis.UNITAIRE,
      quantite: consommable.quantite,
      prixUnitaire: consommable.prixUnitaire,
      total: consommable.total,
      unite: consommable.unite,
      estSousLigne: true,
      parentId: lignes[ligneActiveIndex].id,
    }));

    // REMPLACER (et non ajouter) les sous-lignes de la ligne parente
    // Cela permet de recalculer les consommables si la taille ou la désignation change
    setValue(`lignes.${ligneActiveIndex}.sousLignes`, nouvellesSousLignes);

    // Fermer le modal
    setCalculateurConsommablesOpen(false);
    setLigneActiveIndex(null);
  };

  // Fonction pour ajouter un pack de travaux depuis les templates de pièces
  const handleAddPackFromTemplate = (lignes: LigneDevis[]) => {
    // Ajouter toutes les lignes du pack au devis
    lignes.forEach(ligne => {
      append(ligne);
    });
  };

  // Callback après injection réussie : recharger le devis
  const handleInjectionSuccess = () => {
    if (isEditing && id) {
      // Recharger le devis pour afficher les nouvelles lignes
      const devisId = parseInt(id, 10);
      const devis = getDevisById(devisId);
      if (devis) {
        reset({
          clientId: devis.clientId,
          dateEmission: devis.dateEmission.split('T')[0],
          dateCreation: (devis.dateEmission || devis.dateCreation || '').split('T')[0],
          dateValidite: devis.dateValidite.split('T')[0],
          statut: devis.statut,
          tauxTVA: devis.tauxTVA,
          lignes: devis.lignes,
          notes: devis.notes,
          conditions: devis.conditions,
        });
      }
    }
  };

  // Callback quand le wizard est complété
  const handleWizardComplete = (clientId: number, projetId: number) => {
    setSelectedProjetId(projetId);
    setValue('clientId', clientId);
    setShowWizard(false);
  };

  // Afficher le wizard si nécessaire
  if (showWizard && !isEditing) {
    return (
      <DevisWizard
        onComplete={handleWizardComplete}
        initialProjetId={initialProjetId}
      />
    );
  }

  return (
    <Box>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontWeight: 700, color: '#333', mb: 3 }}
      >
        {isEditing ? 'Modifier le devis' : 'Nouveau devis'}
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Informations générales */}
        <Paper sx={{ p: 4, mb: 4, boxShadow: 2, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 4, color: 'theme.palette.primary.main' }}>
            Informations générales
          </Typography>

          <Grid container spacing={4}>
            {/* Affichage en lecture seule du client et projet sélectionnés */}
            {!isEditing && selectedProjetId && (
              <Grid item xs={12}>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      📋 Devis pour :
                    </Typography>
                    <Typography variant="body2">
                      <strong>Client :</strong> {clients.find(c => c.id === watch('clientId'))?.prenom} {clients.find(c => c.id === watch('clientId'))?.nom}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Projet :</strong> {projets.find(p => p.id === selectedProjetId)?.titre} - {projets.find(p => p.id === selectedProjetId)?.adresseChantier}
                    </Typography>
                  </Box>
                </Alert>
              </Grid>
            )}

            {/* Sélecteur de projet en mode édition uniquement */}
            {isEditing && (
              <Grid item xs={12}>
                <ProjetSelector
                  value={selectedProjetId}
                  onChange={(projetId) => {
                    setSelectedProjetId(projetId);
                    setProjetError('');

                    // Mettre à jour le clientId dans le formulaire basé sur le projet sélectionné
                    if (projetId) {
                      const projet = getProjetById(projetId);
                      if (projet) {
                        reset({
                          ...watch(),
                          clientId: projet.clientId,
                        });
                      }
                    }
                  }}
                  error={!!projetError}
                  helperText={projetError}
                />
              </Grid>
            )}

            {/* Sélection du métier */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel sx={{ backgroundColor: 'white', px: 0.5 }}>Corps de métier</InputLabel>
                <Select
                  value={metierSelectionne}
                  label="Corps de métier"
                  onChange={(e) => setMetierSelectionne(e.target.value as CorpsMetier)}
                  sx={{
                    bgcolor: '#f5f5f5',
                    '&:hover': { bgcolor: '#eeeeee' },
                  }}
                >
                  <MenuItem value={CorpsMetier.CARRELEUR}>{CORPS_METIER_LABELS[CorpsMetier.CARRELEUR]}</MenuItem>
                  <MenuItem value={CorpsMetier.PLOMBIER}>{CORPS_METIER_LABELS[CorpsMetier.PLOMBIER]}</MenuItem>
                  <MenuItem value={CorpsMetier.ELECTRICIEN}>{CORPS_METIER_LABELS[CorpsMetier.ELECTRICIEN]}</MenuItem>
                  <MenuItem value={CorpsMetier.MACON}>{CORPS_METIER_LABELS[CorpsMetier.MACON]}</MenuItem>
                  <MenuItem value={CorpsMetier.PEINTRE}>{CORPS_METIER_LABELS[CorpsMetier.PEINTRE]}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="dateCreation"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Date de création"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="dateValidite"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Date de validité"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="statut"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel sx={{ backgroundColor: 'white', px: 0.5 }}>Statut</InputLabel>
                    <Select {...field} label="Statut">
                      <MenuItem value={StatutDevis.BROUILLON}>Brouillon</MenuItem>
                      <MenuItem value={StatutDevis.ENVOYE}>Envoyé</MenuItem>
                      <MenuItem value={StatutDevis.ACCEPTE}>Accepté</MenuItem>
                      <MenuItem value={StatutDevis.REFUSE}>Refusé</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {/* Alert TVA si l'utilisateur est exonéré */}
            {user?.isExemptFromTVA && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <strong>Franchise en base de TVA :</strong> Votre entreprise est exonérée de TVA.
                  Le taux de TVA est automatiquement défini à 0% pour tous vos devis.
                  Conformément à l'article 293 B du CGI, vous devez mentionner sur vos devis :
                  "TVA non applicable, art. 293 B du CGI".
                </Alert>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <Controller
                name="tauxTVA"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Taux TVA (%)"
                    type="number"
                    disabled={user?.isExemptFromTVA}
                    helperText={
                      user?.isExemptFromTVA
                        ? "TVA non applicable (franchise en base)"
                        : "Taux de TVA applicable"
                    }
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                    sx={{
                      '& .MuiInputBase-input': {
                        backgroundColor: user?.isExemptFromTVA ? '#f9fafb' : 'white',
                      },
                    }}
                  />
                )}
              />
            </Grid>

            {/* Champs de remise */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}>Remise (optionnel)</Divider>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="typeRemise"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel sx={{ backgroundColor: 'white', px: 0.5 }}>Type de remise</InputLabel>
                    <Select {...field} label="Type de remise" value={field.value || ''}>
                      <MenuItem value="">Aucune remise</MenuItem>
                      <MenuItem value={TypeRemise.POURCENTAGE}>Pourcentage (%)</MenuItem>
                      <MenuItem value={TypeRemise.MONTANT_FIXE}>Montant fixe (€)</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="valeurRemise"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={watch('typeRemise') === TypeRemise.POURCENTAGE ? 'Valeur (%)' : 'Montant (€)'}
                    type="number"
                    inputProps={{
                      min: 0,
                      max: watch('typeRemise') === TypeRemise.POURCENTAGE ? 100 : undefined,
                      step: 0.01
                    }}
                    disabled={!watch('typeRemise')}
                    helperText={watch('typeRemise') ? (watch('typeRemise') === TypeRemise.POURCENTAGE ? 'Saisissez un pourcentage entre 0 et 100' : 'Saisissez un montant en euros') : 'Sélectionnez d\'abord un type de remise'}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Modal Calculateur de fournitures universel (supporte tous les métiers) */}
        <CalculateurConsommablesUniversel
          open={calculateurConsommablesOpen}
          onClose={() => {
            setCalculateurConsommablesOpen(false);
            setLigneActiveIndex(null);
          }}
          quantite={surfaceLigneActive}
          designation={ligneActiveIndex !== null ? lignes[ligneActiveIndex]?.description : undefined}
          tailleReference={ligneActiveIndex !== null ? lignes[ligneActiveIndex]?.tailleCarrelage : undefined}
          metier={metierSelectionne}
          onAjouterConsommables={handleAjouterConsommables}
        />

        {/* Calculateur Technique - Bouton d'ouverture
        {isEditing && (
          <Paper sx={{ p: 3, mb: 3, boxShadow: 2, borderRadius: 2, background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>
                  🧮 Calculateur Technique de Carrelage
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Calculez automatiquement les quantités de carrelage, colle et joint en fonction des dimensions du chantier et du carreau.
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={() => setCalculateurOpen(true)}
                sx={{
                  ml: 2,
                  background: 'linear-gradient(135deg, theme.palette.primary.main 0%, ${theme.palette.secondary.main} 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #66438f 100%)',
                  },
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                }}
              >
                Ouvrir le calculateur
              </Button>
            </Box>
          </Paper>
        )}*/}

        {/* Modal du Calculateur Tec}
        <CalculateurTechnique
          open={calculateurOpen}
          onClose={() => setCalculateurOpen(false)}
          devisId={isEditing && id ? parseInt(id, 10) : undefined}
          onSuccess={handleInjectionSuccess}
        />

        {/* Lignes du devis - Liste de cartes */}
        <Paper sx={{ p: 3, mb: 3, boxShadow: 2, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'theme.palette.primary.main', mb: 3 }}>
            Lignes du devis
          </Typography>

          {fields.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4, bgcolor: '#fafafa', borderRadius: 2, mb: 3 }}>
              <Typography color="text.secondary">
                Aucune ligne. Cliquez sur "Ajouter une ligne ou Packs par pièce" pour commencer.
              </Typography>
            </Box>
          )}

          {fields.map((field, index) => {
            const ligne = lignes[index];
            const ligneTotal = ligne ? calculateLigneTotal(ligne) : 0;

            return (
              <Paper
                key={field.id}
                elevation={2}
                sx={{
                  p: 2.5,
                  mb: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  '&:hover': {
                    boxShadow: 3,
                    borderColor: 'theme.palette.primary.main',
                  },
                  transition: 'all 0.3s',
                }}
              >
                {/* En-tête avec numéro et bouton supprimer */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'theme.palette.primary.main' }}>
                    📋 Ligne #{index + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => remove(index)}
                    sx={{
                      '&:hover': {
                        bgcolor: 'error.light',
                        color: 'white',
                      },
                    }}
                  >
                    <DeleteOutlined />
                  </IconButton>
                </Box>

                <Grid container spacing={2}>
                  {/* Description */}
                  <Grid item xs={12} sm={8}>
                    <Controller
                      name={`lignes.${index}.description`}
                      control={control}
                      rules={{ required: 'Description requise' }}
                      render={({ field: { onChange, value } }) => {
                        // Combiner les désignations prédéfinies selon le métier et les produits du catalogue
                        const designationsMetier = getDesignationsByMetier(metierSelectionne);
                        const optionsCombinees = [
                          ...designationsMetier,
                          ...produits.map(p => p.designation),
                        ];

                        return (
                        <Autocomplete
                          freeSolo
                          options={optionsCombinees}
                          value={value || ''}
                          onChange={(_, newValue) => {
                            // Conserver la taille actuelle si elle existe
                            const tailleActuelle = lignes[index]?.tailleCarrelage;
                            let descriptionFinale = newValue || '';

                            // Ajouter la taille à la description si elle existe
                            if (tailleActuelle && descriptionFinale && !descriptionFinale.includes(tailleActuelle)) {
                              descriptionFinale = `${descriptionFinale} ${tailleActuelle}`;
                            }

                            onChange(descriptionFinale);

                            // Si c'est un produit du catalogue, pré-remplir le prix
                            const produitCatalogue = produits.find(p => p.designation === newValue);
                            if (produitCatalogue) {
                              setValue(`lignes.${index}.prixUnitaire`, produitCatalogue.prixUnitaireHT);
                              setValue(`lignes.${index}.unite`, produitCatalogue.uniteMesure);

                              // Déterminer le type en fonction de l'unité de mesure
                              if (produitCatalogue.uniteMesure === 'm²') {
                                setValue(`lignes.${index}.type`, TypeLigneDevis.SURFACE);
                              } else if (produitCatalogue.uniteMesure === 'ml') {
                                setValue(`lignes.${index}.type`, TypeLigneDevis.UNITAIRE);
                              } else if (produitCatalogue.uniteMesure === 'kg' || produitCatalogue.uniteMesure === 'L' || produitCatalogue.uniteMesure === 'pièce' || produitCatalogue.uniteMesure === 'unité') {
                                setValue(`lignes.${index}.type`, TypeLigneDevis.PIECE);
                              } else if (produitCatalogue.uniteMesure === 'forfait') {
                                setValue(`lignes.${index}.type`, TypeLigneDevis.FORFAIT);
                              } else {
                                // Par défaut, si l'unité n'est pas reconnue, utiliser PIECE
                                setValue(`lignes.${index}.type`, TypeLigneDevis.PIECE);
                              }
                            } else {
                              // Détecter automatiquement le type en fonction de la désignation
                              if (newValue) {
                                const designation = newValue.toLowerCase();
                                if (designation.includes('plinthe') || designation.includes('baguette') || designation.includes('nez de marche')) {
                                  setValue(`lignes.${index}.type`, TypeLigneDevis.UNITAIRE);
                                  setValue(`lignes.${index}.unite`, 'ml');
                                } else if (designation.includes('forfait') || designation.includes('frais') || designation.includes('déplacement') || designation.includes('évacuation') || designation.includes('nettoyage') || designation.includes('habillage') || designation.includes('création')) {
                                  setValue(`lignes.${index}.type`, TypeLigneDevis.FORFAIT);
                                  setValue(`lignes.${index}.unite`, 'forfait');
                                } else if (designation.includes('pose') || designation.includes('carrelage') || designation.includes('faïence') || designation.includes('ragréage')) {
                                  setValue(`lignes.${index}.type`, TypeLigneDevis.SURFACE);
                                  setValue(`lignes.${index}.unite`, 'm²');
                                }
                              }
                            }
                          }}
                          onInputChange={(_, newInputValue) => {
                            const tailleActuelle = lignes[index]?.tailleCarrelage;
                            let descriptionFinale = newInputValue;

                            // Ajouter la taille à la description si elle existe
                            if (tailleActuelle && descriptionFinale && !descriptionFinale.includes(tailleActuelle)) {
                              descriptionFinale = `${descriptionFinale} ${tailleActuelle}`;
                            }

                            onChange(descriptionFinale);

                            // Détecter automatiquement le type en fonction de la saisie
                            if (newInputValue) {
                              const designation = newInputValue.toLowerCase();
                              if (designation.includes('plinthe') || designation.includes('baguette') || designation.includes('nez de marche')) {
                                setValue(`lignes.${index}.type`, TypeLigneDevis.UNITAIRE);
                                setValue(`lignes.${index}.unite`, 'ml');
                              } else if (designation.includes('forfait') || designation.includes('frais') || designation.includes('déplacement') || designation.includes('évacuation') || designation.includes('nettoyage') || designation.includes('habillage') || designation.includes('création')) {
                                setValue(`lignes.${index}.type`, TypeLigneDevis.FORFAIT);
                                setValue(`lignes.${index}.unite`, 'forfait');
                              } else if (designation.includes('pose') || designation.includes('carrelage') || designation.includes('faïence') || designation.includes('ragréage')) {
                                setValue(`lignes.${index}.type`, TypeLigneDevis.SURFACE);
                                setValue(`lignes.${index}.unite`, 'm²');
                              }
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Désignation"
                              error={!!errors.lignes?.[index]?.description}
                              helperText={errors.lignes?.[index]?.description?.message}
                              size="small"
                              placeholder="Tapez ou sélectionnez une désignation..."
                            />
                          )}
                          size="small"
                          componentsProps={{
                            paper: {
                              sx: {
                                '& .MuiAutocomplete-option': {
                                  fontSize: '0.875rem',
                                  color: '#666',
                                },
                              },
                            },
                          }}
                        />
                        );
                      }}
                    />
                  </Grid>

                  {/* Taille de carrelage - UNIQUEMENT pour Carreleur */}
                  {metierSelectionne === CorpsMetier.CARRELEUR && (
                    <Grid item xs={12} sm={4}>
                      <Controller
                        name={`lignes.${index}.tailleCarrelage`}
                        control={control}
                        render={({ field: { onChange, value } }) => {
                          const taillesMetier = getTaillesByMetier(metierSelectionne);
                          const labelTaille = getTailleLabel(metierSelectionne);

                          return (
                            <Autocomplete
                              freeSolo
                              options={taillesMetier}
                              value={value || ''}
                              onChange={(_, newValue) => {
                                // Récupérer la description actuelle
                                const descriptionActuelle = lignes[index]?.description || '';

                                let nouvelleDescription = descriptionActuelle;

                                // Supprimer toutes les tailles possibles du format XXxYY ou XX*YY
                                // Pattern pour matcher les tailles: nombre + x ou * + nombre
                                const taillePattern = /\s*\d+\.?\d*\s*[x×*]\s*\d+\.?\d*\s*/gi;
                                nouvelleDescription = nouvelleDescription.replace(taillePattern, ' ');

                                // Nettoyer les espaces multiples
                                nouvelleDescription = nouvelleDescription.replace(/\s+/g, ' ').trim();

                                // Ajouter la nouvelle taille si elle existe
                                if (newValue) {
                                  nouvelleDescription = `${nouvelleDescription} ${newValue}`.trim();
                                }

                                // Mettre à jour d'abord la taille
                                onChange(newValue || '');

                                // Puis mettre à jour la description
                                setValue(`lignes.${index}.description`, nouvelleDescription);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={labelTaille}
                                  size="small"
                                  placeholder="Ex: 60x60"
                                />
                              )}
                              size="small"
                              componentsProps={{
                                paper: {
                                  sx: {
                                    '& .MuiAutocomplete-option': {
                                      fontSize: '0.875rem',
                                      color: '#666',
                                    },
                                  },
                                },
                              }}
                            />
                          );
                        }}
                      />
                    </Grid>
                  )}

                  {/* Type */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Controller
                      name={`lignes.${index}.type`}
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small">
                          <InputLabel>Type</InputLabel>
                          <Select {...field} label="Type">
                            {/* Type Surface pour carreleurs et maçons */}
                            {(metierSelectionne === CorpsMetier.CARRELEUR || metierSelectionne === CorpsMetier.MACON) && (
                              <MenuItem value={TypeLigneDevis.SURFACE}>Surface (m²)</MenuItem>
                            )}
                            <MenuItem value={TypeLigneDevis.UNITAIRE}>Mètre Linéaire</MenuItem>
                            <MenuItem value={TypeLigneDevis.PIECE}>Unité (pièce)</MenuItem>
                            <MenuItem value={TypeLigneDevis.FORFAIT}>Forfait</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>

                  {/* Champs spécifiques au type SURFACE - Pour carreleurs et maçons */}
                  {ligne?.type === TypeLigneDevis.SURFACE && (metierSelectionne === CorpsMetier.CARRELEUR || metierSelectionne === CorpsMetier.MACON) && (
                    <>
                      {/* Mode de saisie */}
                      <Grid item xs={12} sm={6} md={4}>
                        <Controller
                          name={`lignes.${index}.modeSaisieSurface`}
                          control={control}
                          render={({ field }) => (
                            <FormControl fullWidth size="small">
                              <InputLabel>Mode de saisie</InputLabel>
                              <Select {...field} label="Mode de saisie">
                                <MenuItem value="direct">📏 Surface directe</MenuItem>
                                <MenuItem value="dimensions">📐 Longueur × Largeur</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        />
                      </Grid>

                      {/* Saisie par dimensions */}
                      {(ligne.modeSaisieSurface === 'dimensions' || !ligne.modeSaisieSurface) && (
                        <>
                          <Grid item xs={6} sm={3} md={2}>
                            <Controller
                              name={`lignes.${index}.longueur`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  label="Longueur (m)"
                                  type="number"
                                  size="small"
                                  inputProps={{ min: 0, step: 0.01 }}
                                />
                              )}
                            />
                          </Grid>

                          <Grid item xs={6} sm={3} md={2}>
                            <Controller
                              name={`lignes.${index}.largeur`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  label="Largeur (m)"
                                  type="number"
                                  size="small"
                                  inputProps={{ min: 0, step: 0.01 }}
                                />
                              )}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <TextField
                              fullWidth
                              label="Surface calculée"
                              size="small"
                              value={
                                ligne.longueur && ligne.largeur
                                  ? `${((Number(ligne.longueur) || 0) * (Number(ligne.largeur) || 0)).toFixed(2)} m²`
                                  : '0 m²'
                              }
                              InputProps={{
                                readOnly: true,
                              }}
                              sx={{ bgcolor: '#F3F4F6' }}
                            />
                          </Grid>
                        </>
                      )}

                      {/* Saisie directe de surface */}
                      {ligne.modeSaisieSurface === 'direct' && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Controller
                            name={`lignes.${index}.surface`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                label="Surface (m²)"
                                type="number"
                                size="small"
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            )}
                          />
                        </Grid>
                      )}

                      {/* Switch pertes - uniquement pour le carreleur */}
                      {metierSelectionne === CorpsMetier.CARRELEUR && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Controller
                            name={`lignes.${index}.avecPertes`}
                            control={control}
                            render={({ field }) => (
                              <FormControlLabel
                                control={<Switch {...field} checked={field.value} />}
                                label="+ 10% pertes"
                              />
                            )}
                          />
                        </Grid>
                      )}

                      {/* Surface finale */}
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          label="Surface finale"
                          size="small"
                          value={
                            (() => {
                              let surfaceBase = 0;
                              if (ligne.modeSaisieSurface === 'direct' && ligne.surface) {
                                surfaceBase = Number(ligne.surface) || 0;
                              } else if (ligne.longueur && ligne.largeur) {
                                surfaceBase = (Number(ligne.longueur) || 0) * (Number(ligne.largeur) || 0);
                              }
                              const surfaceFinale = ligne.avecPertes ? surfaceBase * 1.1 : surfaceBase;
                              return `${Number(surfaceFinale).toFixed(2)} m²`;
                            })()
                          }
                          InputProps={{
                            readOnly: true,
                          }}
                          sx={{ bgcolor: '#FEF3C7' }}
                        />
                      </Grid>

                      {/* Bouton pour calculer les consommables */}
                      {ligne.type === TypeLigneDevis.SURFACE && (
                        <Grid item xs={12}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CalculateOutlined />}
                            onClick={() => {
                              let surfaceBase = 0;
                              if (ligne.modeSaisieSurface === 'direct' && ligne.surface) {
                                surfaceBase = Number(ligne.surface) || 0;
                              } else if (ligne.longueur && ligne.largeur) {
                                surfaceBase = (Number(ligne.longueur) || 0) * (Number(ligne.largeur) || 0);
                              }
                              const surfaceFinale = ligne.avecPertes ? surfaceBase * 1.1 : surfaceBase;

                              if (surfaceFinale > 0) {
                                setSurfaceLigneActive(surfaceFinale);
                                setLigneActiveIndex(index);
                                setCalculateurConsommablesOpen(true);
                              }
                            }}
                            disabled={
                              (() => {
                                let surfaceBase = 0;
                                if (ligne.modeSaisieSurface === 'direct' && ligne.surface) {
                                  surfaceBase = Number(ligne.surface) || 0;
                                } else if (ligne.longueur && ligne.largeur) {
                                  surfaceBase = (Number(ligne.longueur) || 0) * (Number(ligne.largeur) || 0);
                                }
                                return surfaceBase === 0;
                              })()
                            }
                            sx={{
                              borderColor: '#F59E0B',
                              color: '#F59E0B',
                              '&:hover': {
                                borderColor: '#D97706',
                                bgcolor: '#FEF3C7',
                              },
                            }}
                          >
                            Calculer les consommables
                          </Button>
                          {ligne.sousLignes && ligne.sousLignes.length > 0 && (
                            <Typography variant="caption" sx={{ ml: 2, color: '#10B981' }}>
                              ✓ {ligne.sousLignes.length} consommable(s) ajouté(s)
                            </Typography>
                          )}
                        </Grid>
                      )}
                    </>
                  )}

                  {/* Champs spécifiques au type UNITAIRE et PIECE */}
                  {(ligne?.type === TypeLigneDevis.UNITAIRE || ligne?.type === TypeLigneDevis.PIECE) && (
                    <>
                      <Grid item xs={12} sm={6} md={4}>
                        <Controller
                          name={`lignes.${index}.quantite`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label={ligne?.type === TypeLigneDevis.UNITAIRE ? "Quantité (ml)" : "Quantité (pièces)"}
                              type="number"
                              size="small"
                              inputProps={{ min: 0, step: 1 }}
                            />
                          )}
                        />
                      </Grid>
                    </>
                  )}

                  {/* Bouton pour calculer les consommables (PLOMBERIE / ÉLECTRICITÉ / MAÇONNERIE UNITAIRE seulement) */}
                  {(metierSelectionne === CorpsMetier.PLOMBIER ||
                    metierSelectionne === CorpsMetier.ELECTRICIEN ||
                    (metierSelectionne === CorpsMetier.MACON && ligne?.type === TypeLigneDevis.UNITAIRE)) &&
                   (ligne?.type === TypeLigneDevis.UNITAIRE || ligne?.type === TypeLigneDevis.SURFACE) && (
                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CalculateOutlined />}
                        onClick={() => {
                          const quantiteActuelle = Number(ligne.quantite) || 0;
                          if (quantiteActuelle > 0) {
                            setSurfaceLigneActive(quantiteActuelle);
                            setLigneActiveIndex(index);
                            setCalculateurConsommablesOpen(true);
                          }
                        }}
                        disabled={!ligne.quantite || Number(ligne.quantite) === 0}
                        sx={{
                          borderColor: '#F59E0B',
                          color: '#F59E0B',
                          '&:hover': {
                            borderColor: '#D97706',
                            bgcolor: '#FEF3C7',
                          },
                        }}
                      >
                        Calculer les consommables
                      </Button>
                      {ligne.sousLignes && ligne.sousLignes.length > 0 && (
                        <Typography variant="caption" sx={{ ml: 2, color: '#10B981' }}>
                          ✓ {ligne.sousLignes.length} consommable(s) ajouté(s)
                        </Typography>
                      )}
                    </Grid>
                  )}

                  {/* Prix unitaire */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Controller
                      name={`lignes.${index}.prixUnitaire`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label={
                            ligne?.type === TypeLigneDevis.FORFAIT
                              ? 'Prix forfaitaire'
                              : 'Prix unitaire (€)'
                          }
                          type="number"
                          size="small"
                          inputProps={{ min: 0, step: 0.01 }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">€</InputAdornment>,
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Total ligne */}
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      label="💰 Total ligne"
                      size="small"
                      value={formatEuro(ligneTotal)}
                      InputProps={{
                        readOnly: true,
                        sx: { bgcolor: '#FEF3C7', fontWeight: 700 },
                      }}
                    />
                  </Grid>
                </Grid>

                {/* Affichage des sous-lignes (consommables) */}
                {ligne.sousLignes && ligne.sousLignes.length > 0 && (
                  <Box sx={{ mt: 2, pl: 3, borderLeft: '3px solid #F59E0B' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', mb: 1, display: 'block' }}>
                      📦 Consommables associés :
                    </Typography>
                    {ligne.sousLignes.map((sousLigne, sousIndex) => (
                      <Box
                        key={sousLigne.id}
                        sx={{
                          bgcolor: '#f5f5f5',
                          p: 1.5,
                          mb: 1,
                          borderRadius: 1,
                          border: '1px solid #e0e0e0',
                        }}
                      >
                        {/* Description et bouton supprimer */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#555', flex: 1, pr: 1 }}>
                            → {sousLigne.description}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              // Supprimer la sous-ligne
                              const nouvellesSousLignes = ligne.sousLignes?.filter((_, i) => i !== sousIndex) || [];
                              setValue(`lignes.${index}.sousLignes`, nouvellesSousLignes);
                            }}
                            sx={{ color: 'error.main', p: 0.5 }}
                          >
                            <DeleteOutlined fontSize="small" />
                          </IconButton>
                        </Box>

                        {/* Informations responsive */}
                        <Box sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: { xs: 1, sm: 2 },
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}>
                          <Typography variant="caption" sx={{ color: '#666' }}>
                            {sousLigne.quantite} {sousLigne.unite || ''}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#666', display: { xs: 'none', sm: 'block' } }}>
                            {formatEuro(sousLigne.prixUnitaire)} / {sousLigne.unite || 'unité'}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#F59E0B' }}>
                            {formatEuro(calculateLigneTotal(sousLigne))}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            );
          })}

          {/* Boutons Ajouter une ligne et Templates de pièces */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<span style={{ fontSize: '1.2rem' }}>🏠</span>}
              onClick={() => setRoomTemplatePickerOpen(true)}
              sx={{
                borderColor: '#6366F1',
                color: '#6366F1',
                '&:hover': {
                  borderColor: '#4F46E5',
                  bgcolor: '#EEF2FF',
                },
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
              }}
            >
              Packs par pièce
            </Button>

            <Button
              variant="contained"
              startIcon={<AddCircleOutline />}
              onClick={addLigne}
              sx={{
                bgcolor: '#F59E0B',
                '&:hover': { bgcolor: '#D97706' },
                textTransform: 'none',
              }}
            >
              Ajouter une ligne
            </Button>
          </Box>
        </Paper>

        {/* Modal de sélection de templates par pièce */}
        <RoomTemplatePicker
          open={roomTemplatePickerOpen}
          onClose={() => setRoomTemplatePickerOpen(false)}
          metier={metierSelectionne}
          onAddPack={handleAddPackFromTemplate}
        />

        {/* Totaux */}
        <Paper sx={{ p: 4, mb: 3, bgcolor: '#F9FAFB', boxShadow: 2, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#374151', mb: 3 }}>
            💰 Totaux
          </Typography>
          <Grid container item spacing={3}>
            <Grid xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                <Typography variant="body1">Sous-total HT:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formatEuro(totals.sousTotal)}
                </Typography>
              </Box>
            </Grid>

            {/* Afficher la remise si elle existe */}
            {(() => {
              const typeRemise = watch('typeRemise');
              const valeurRemise = watch('valeurRemise');
              if (typeRemise && valeurRemise && valeurRemise > 0) {
                const calculatedTotals = calculateDevisTotalsWithRemise(lignes, tauxTVA, typeRemise, valeurRemise);
                return (
                  <>
                    <Grid xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                        <Typography variant="body1" color="error">
                          Remise ({typeRemise === TypeRemise.POURCENTAGE ? `${valeurRemise}%` : 'montant fixe'}):
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.main' }}>
                          - {formatEuro(calculatedTotals.montantRemise)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>Montant HT après remise:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                          {formatEuro(calculatedTotals.montantHTApresRemise)}
                        </Typography>
                      </Box>
                    </Grid>
                  </>
                );
              }
              return null;
            })()}

            <Grid xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                <Typography variant="body1">
                  {user?.isExemptFromTVA ? 'TVA non applicable (art. 293 B CGI)' : `TVA (${tauxTVA}%)`}:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {user?.isExemptFromTVA ? '0,00 €' : formatEuro(totals.montantTVA)}
                </Typography>
              </Box>
            </Grid>
            <Grid xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  TOTAL TTC:
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#10B981' }}>
                  {formatEuro(totals.totalTTC)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Notes et conditions */}
        <Paper sx={{ p: 4, mb: 3, boxShadow: 2, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3, color: 'theme.palette.primary.main' }}>
            📝 Notes et conditions
          </Typography>
          <Grid container item spacing={3}>
            <Grid xs={12}>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Notes"
                    multiline
                    rows={3}
                    sx={{ mb: 2 }}
                  />
                )}
              />
            </Grid>

            <Grid xs={12}>
              <Controller
                name="conditions"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Conditions"
                    multiline
                    rows={3}
                    sx={{ mb: 1 }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Boutons d'action */}
        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            startIcon={<SaveOutlined />}
            sx={{
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              bgcolor: '#F59E0B',
              '&:hover': { bgcolor: '#D97706' },
            }}
          >
            {isEditing ? 'Modifier' : 'Enregistrer'}
          </Button>

          <Button
            variant="outlined"
            size="large"
            fullWidth
            startIcon={<CancelOutlined />}
            onClick={() => navigate('/devis')}
            sx={{
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
            }}
          >
            Annuler
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default DevisFormPage;
